import { useEffect, useState } from 'react';

import CLOSE from '../../../../assets/imgs/icon/close.svg';
import Button from '../../../../GlobalComponents/Button';
import { Content } from '../../../../GlobalComponents/Content';
import { ErrorMessage } from '../../../../GlobalComponents/ErrorMessage';
import { Flex } from '../../../../GlobalComponents/Flex';
import { IconButton } from '../../../../GlobalComponents/IconButton';
import { Title } from '../../../../GlobalComponents/Title';
import { useAppSelector } from '../../../../reduxstore/hooks';
import { moneyToCents } from '../../../../utils/moneyToCents';
import { useCreateStripePaymentIntentMutation } from '../../../graphql';
import { Chip, ModalContent } from '../components/Card';

import { CheckoutClient } from './CheckoutClient';
import CustomChip from './CustomChip';
import { Modal } from '../../../../GlobalComponents/Modal';
import { PaymentIntentDetails } from '../../../../Checkout/views/PaymentGateway';

interface IAddBalanceProps {
	onClose?: () => void;
	cardNumber: string | number;
}

const AddBalance = ({ onClose, cardNumber }: IAddBalanceProps): JSX.Element => {
	const profile = useAppSelector(state => state.accountReducer.profile);

	const cardData = profile.cards.find(
		card => card.card_number === cardNumber
	) as Card;

	const [formData, setFormData] = useState({} as IBalanceData);
	const [showModal, setShowModal] = useState<boolean>(false);
	const [isLoading, setLoading] = useState<boolean>(false);
	const [paymentDetails, setPaymentDetails] = useState<PaymentIntentDetails>({
		clientSecret: undefined,
		paymentIntentId: undefined,
		id: undefined
	});
	const [formError, setFormError] = useState<string | null>('');
	const [paymentIntent] = useCreateStripePaymentIntentMutation({
		errorPolicy: 'none'
	});

	useEffect(() => {
		if (cardData) {
			const initialState = {
				balanceAmount: cardData.cash,
				giftCardNumber: cardData.card_number,
				balanceToAdd: 0,
				token: ''
			} as IBalanceData;

			setFormData(initialState);
		}
	}, [cardData]);

	const validateForm = (): boolean => {
		let formIsValid = true;

		if (formData.balanceToAdd === 0) {
			formIsValid = false;
			setFormError('Please select an amount to add to the card.');
		}

		setFormData({
			...formData
		});
		return formIsValid;
	};

	const initiatePayment = () => {
		if (validateForm()) {
			setLoading(true);
			paymentIntent({
				variables: {
					profileId: Number(profile.id),
					currency: 'usd',
					amount: moneyToCents(`${formData.balanceToAdd}`),
					description: `BIGGBY Card Balance Reload`,
					giftCardNumber: `${formData.giftCardNumber}`
				}
			})
				.then(res => {
					if (res.errors) {
						setFormError(res.errors[0].message);
					} else
						setPaymentDetails({
							clientSecret:
								res?.data?.createStripePaymentIntent
									.clientSecret,
							paymentIntentId:
								res?.data?.createStripePaymentIntent
									.paymentIntentId,
							id: res?.data?.createStripePaymentIntent.id
						});
				})
				.catch(err => {
					setFormError(err.message);
				})
				.finally(() => {
					setLoading(false);
				});
		} else
			setPaymentDetails({
				clientSecret: undefined,
				paymentIntentId: undefined,
				id: undefined
			});
	};

	const onSelectBalance = (amount: number) => {
		setFormError(null);
		setFormData(formData => ({
			...formData,
			balanceToAdd: amount
		}));
	};

	const onToggleCustomBal = () => {
		setShowModal(!showModal);
	};

	const onBack = () => {		
		setPaymentDetails({
			clientSecret: undefined,
			paymentIntentId: undefined,
			id: undefined
		})
	};

	return (
		<ModalContent onClose={onClose}>
			<Flex $flexDirection="column" $margin="0 0 16px 0" $width="100%">
				<Title>
					<Flex
						$flexDirection="row"
						$width="100%"
						$alignItems="center"
						$justifyContent="space-between"
					>
						Add Balance
						<IconButton url={CLOSE} alt="X" onClick={onClose} />
					</Flex>
				</Title>

				{formError && <ErrorMessage message={formError} />}

				<Content $fontSize={16} $fontWeight={700} $padding="0 0 24px 0">
					<Flex $flexDirection="column" $rowGap="8px">
						<Content>Card Number: {cardNumber}</Content>
						<Content>Current Balance: ${cardData?.cash}</Content>
						<Content>
							Balance to Add: ${formData.balanceToAdd}
						</Content>
					</Flex>
				</Content>

				{!paymentDetails.clientSecret && !isLoading && (
					<Flex
						$columnGap="8px"
						$rowGap="16px"
						$flexDirection="row"
						$flexWrap="nowrap"
						$margin="0 0 24px 0"
						$placeContent="center space-between"
						$alignItems="center"
					>
						<Chip onClick={() => onSelectBalance(5)}>$5</Chip>
						<Chip onClick={() => onSelectBalance(10)}>$10</Chip>
						<Chip onClick={() => onSelectBalance(20)}>$20</Chip>
						<Chip onClick={() => onSelectBalance(25)}>$25</Chip>
						<Chip onClick={() => onToggleCustomBal()}>Custom</Chip>
					</Flex>
				)}

				{formData.balanceToAdd > 0 && !paymentDetails.clientSecret && (
					<Flex
						$flexDirection="row"
						$alignItems="center"
						$placeContent="end"
					>
						<Button
							onClick={initiatePayment}
							$isLoading={isLoading}
						>
							CONFIRM
						</Button>
					</Flex>
				)}
			</Flex>

			{paymentDetails.clientSecret && (
				<CheckoutClient
					giftCardNumber={formData.giftCardNumber}
					amount={moneyToCents(`${formData.balanceToAdd}`)}
					paymentDetails={paymentDetails}
					onClose={onClose}
					onBack={onBack}
				/>
			)}

			<Modal
				isOpen={showModal}
				toggleHandler={() => onToggleCustomBal()}
				size="md"
			>
				<CustomChip
					cardValue={String(formData.balanceToAdd || '')}
					onAmountChange={onSelectBalance}
					onToggleCustomBal={onToggleCustomBal}
				/>
			</Modal>
		</ModalContent>
	);
};

export default AddBalance;

export interface IBalanceData {
	balanceAmount: number;
	giftCardNumber: number;
	balanceToAdd: number;
	token?: string;
}
